import type { FC, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { CircleIconWrapper } from '@ContractBuilder/contract-builder-header/components/CircleIconWrapper';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { useEntityStore } from '@ContractBuilder/store';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import { useGuidanceStatus } from '@features/pbqa-eval';
import { getDocumentMode, isSubmission } from '@helpers/getDocumentMode';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import clsx from 'clsx';
import { Check, Radar } from 'lucide-react';

/**
 * Open-suggestion count on the header icon, in the guidance accent colour;
 * a tick once a completed scan has nothing left to review.
 */
const GuidanceCountBadge: FC<{ count: number; isComplete: boolean; children: ReactNode }> = ({
  count,
  isComplete,
  children,
}) => {
  if (count === 0 && !isComplete) return <>{children}</>;
  if (count === 0) {
    return (
      <div className="relative inline-flex">
        {children}
        <div
          className="pointer-events-none absolute -top-0.5 -right-1 flex size-4 items-center justify-center rounded-full bg-violet-600 text-white"
          data-testid="pbqa-complete-badge"
          aria-label="All guidance suggestions reviewed"
          role="img"
        >
          <Check className="size-2.5" strokeWidth={3} aria-hidden />
        </div>
      </div>
    );
  }
  return (
    <div className="relative inline-flex">
      {children}
      <div
        className={clsx(
          'pointer-events-none absolute -top-1 -right-1.5 flex size-5 items-center justify-center rounded-full bg-violet-600 text-xs leading-5 font-medium text-white',
          count > 99 && 'text-[8px]!',
        )}
        data-testid="pbqa-count-badge"
      >
        {count > 99 ? '99+' : count}
      </div>
    </div>
  );
};

/**
 * Contract Guidance drawer toggle — mirrors ShowCommentsAndActivitySection.
 * Gated by the PbqaEval feature flag AND the caller's feature_previews
 * allowlist access (limited preview) + only shown when viewing a submission.
 * The icon goes inactive during block-edit mode (same UX as Comments) because
 * the drawer is hidden by the edit layer.
 */
export const ShowPbqaEvalSection = () => {
  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const { closeDrawer, isDrawerOpen, openDrawer, state } = useDrawerStore(
    ({ closeDrawer, isOpen, openDrawer, state }) => ({
      closeDrawer,
      isDrawerOpen: isOpen,
      openDrawer,
      state,
    }),
  );
  const submission = useEntityStore(({ submission }) => submission);
  const { pathname } = useLocation();
  const getFeatureFlagState = useFeatureFlags();
  // Either preview cohort gets the drawer; what it contains differs (see
  // PbqaEvalController's showEvalControls).
  const { pbqaEval, pbqaReview } = useFeatureAccess();

  const isPbqaEnabled = getFeatureFlagState('PbqaEval') && (pbqaEval || pbqaReview) && isSubmission(pathname);
  const isPbqaDrawerOpen = isDrawerOpen && state?.type === DrawerLayoutComponent.PbqaEval;
  const isEditing = editingBlockId !== undefined;

  // The entity store holds whichever document is open — on an endorsement
  // path that is the endorsement, under the same `submission` slot. Leaving
  // an endorsement for its parent flips the URL a render before the store
  // catches up, and for that render the URL says "submission" while the
  // store still says the endorsement. Key everything on the id in the URL
  // and wait until the store agrees, or the header would ask
  // `/submissions/<endorsementId>/eval_runs` and 404.
  const routeSubmissionId = getDocumentMode(pathname).ids[0];
  const isStoreCurrent = Boolean(routeSubmissionId) && submission?.id === routeSubmissionId;

  const { openCount, isComplete } = useGuidanceStatus({
    submissionId: routeSubmissionId ?? '',
    contractName: submission?.name,
    enabled: Boolean(isPbqaEnabled && isStoreCurrent),
    isDrawerOpen: isPbqaDrawerOpen,
    rootCounts: {
      runId: submission?.guidanceRunId,
      findingsCount: submission?.guidanceFindingsCount,
      reviewedCount: submission?.guidanceReviewedCount,
    },
    pbqaEval,
  });

  const handleToggleClick = () => {
    if (isDrawerOpen && isPbqaDrawerOpen) {
      return closeDrawer();
    }
    return openDrawer({ type: DrawerLayoutComponent.PbqaEval, props: {}, width: 'wide' });
  };

  if (!submission) {
    return null;
  }

  return (
    <Show when={isPbqaEnabled}>
      <GuidanceCountBadge count={openCount} isComplete={isComplete}>
        <Tooltip content={isPbqaDrawerOpen ? 'Hide contract guidance' : 'Show contract guidance'} disabled={isEditing}>
          <CircleIconWrapper
            data-testid="pbqa-toggle-button"
            icon={Radar}
            isActive={isPbqaDrawerOpen}
            onClick={handleToggleClick}
            wrapperClassName={clsx(isEditing && 'pointer-events-none bg-transparent opacity-50')}
          />
        </Tooltip>
      </GuidanceCountBadge>
    </Show>
  );
};
